import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import { hasPermission } from "../entities/access";
import type { MailboxDetail } from "../entities/mailboxes";
import { shellBootstrapQuery } from "./bootstrap";
import { useSubmissionKey, formatDate } from "./counterpartiesSupport";
import { ImportFailuresPanel } from "./ImportFailuresPanel";
import { MailboxCard, MailboxStatusBadge } from "./MailboxCard";
import { MailboxForm, type MailboxFormValues } from "./MailboxForm";
import { MailboxNotice } from "./MailboxNotice";
import {
  compactMailboxesSearch,
  mailboxKeys,
  newMailboxId,
  validateMailboxesSearch,
  type EnabledFilter,
  type MailboxesSearch,
  type MailboxesTab,
  type OrganizationName,
} from "./mailboxesSupport";
import type { ShellServices } from "./services";
import { ErrorPage } from "./states";
import { TabList } from "./TabList";
import { Select } from "./Select";

type Service = NonNullable<ShellServices["mailboxes"]>;

const tabLabels: Record<MailboxesTab, string> = {
  mailboxes: "Почтовые ящики",
  failures: "Ошибки импорта",
};

const enabledOptions: readonly { value: EnabledFilter; label: string }[] = [
  { value: "all", label: "Все ящики" },
  { value: "enabled", label: "Включённые" },
  { value: "disabled", label: "Отключённые" },
];

function CreateMailbox({
  service,
  onCreated,
  onCancel,
}: {
  service: Service;
  onCreated: (mailbox: MailboxDetail) => void;
  onCancel: () => void;
}) {
  const client = useQueryClient();
  const key = useSubmissionKey();
  const create = useMutation({
    // Вариант мутации хранит введённый пароль; в кэше он не должен задерживаться.
    gcTime: 0,
    mutationFn: async (
      values: Extract<MailboxFormValues, { mode: "create" }>,
    ) => service.create(values.input, key.current()),
    onSuccess: async (created) => {
      key.reset();
      // Введённый пароль остаётся только в состоянии формы, которая сейчас
      // закроется; в кэш попадает ответ Core, где секрета нет.
      create.reset();
      client.setQueryData(mailboxKeys.detail(created.id), created);
      await client.invalidateQueries({
        queryKey: [...mailboxKeys.all, "list"],
      });
      onCreated(created);
    },
  });

  return (
    <div className="cp-card">
      <MailboxForm
        pending={create.isPending}
        error={create.error}
        onChange={() => key.reset()}
        onCancel={() => {
          create.reset();
          onCancel();
        }}
        onSubmit={(values) => {
          if (values.mode === "create") create.mutate(values);
        }}
      />
    </div>
  );
}

function MailboxDirectory({
  service,
  canCreate,
  canSync,
  canViewFailures,
  canSelectSystemSender,
  allOrganizations,
  scopeName,
  organizationName,
  search,
  onSearch,
}: {
  service: Service;
  canCreate: boolean;
  canSync: boolean;
  canViewFailures: boolean;
  canSelectSystemSender: boolean;
  allOrganizations: boolean;
  scopeName?: string;
  organizationName: OrganizationName;
  search: MailboxesSearch;
  onSearch: (patch: Partial<MailboxesSearch>) => void;
}) {
  const list = useQuery({
    queryKey: mailboxKeys.list(search.enabled),
    queryFn: () =>
      service.list(
        search.enabled === "all"
          ? {}
          : { enabled: search.enabled === "enabled" },
      ),
    retry: false,
  });
  const items = useMemo(() => list.data?.data ?? [], [list.data]);
  const creating = search.id === newMailboxId;
  const opened = creating ? undefined : search.id;

  return (
    <div
      className="cp-layout"
      data-detail={search.id === undefined ? "closed" : "open"}
    >
      <div className="cp-list-pane">
        <label className="field cp-field">
          <span>Показывать</span>
          <Select
            value={search.enabled}
            onChange={(event) =>
              onSearch({ enabled: event.target.value as EnabledFilter })
            }
          >
            {enabledOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        {list.isLoading ? <p aria-busy="true">Загружаем ящики…</p> : null}
        {list.isError ? (
          <div className="cp-empty">
            <MailboxNotice
              error={list.error}
              fallback="Не удалось загрузить почтовые ящики."
              onRetry={() => void list.refetch()}
              retrying={list.isFetching}
            />
          </div>
        ) : null}
        {list.isSuccess && items.length === 0 ? (
          <div className="cp-empty">
            <p>
              {search.enabled !== "all"
                ? "Ящиков с таким состоянием нет."
                : allOrganizations
                  ? "Ни в одной из доступных вам организаций почтовых ящиков пока нет."
                  : scopeName === undefined
                    ? "В этой организации почтовых ящиков пока нет."
                    : `В организации «${scopeName}» почтовых ящиков пока нет.`}{" "}
              {search.enabled === "all"
                ? allOrganizations
                  ? "Чтобы добавить ящик, выберите организацию в поле «Рабочая организация» в верхней панели."
                  : "Добавьте ящик, чтобы документооборот начал получать письма."
                : null}
            </p>
            {search.enabled === "all" && canCreate ? (
              <button
                type="button"
                className="button button--secondary button--compact"
                onClick={() => onSearch({ id: newMailboxId })}
              >
                Добавить ящик
              </button>
            ) : null}
          </div>
        ) : null}

        {items.length > 0 ? (
          <>
            <ul className="cp-list" aria-label="Почтовые ящики">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="cp-list__item"
                    aria-current={item.id === opened ? "true" : undefined}
                    onClick={() => onSearch({ id: item.id })}
                  >
                    <span className="cp-list__title">{item.name}</span>
                    <span className="muted">{item.email}</span>
                    <span className="mb-org">
                      {organizationName(item.access_scope_id) ??
                        "Организация недоступна"}
                    </span>
                    <span className="cp-list__meta muted">
                      <MailboxStatusBadge
                        status={item.status}
                        enabled={item.enabled}
                      />
                      <span>
                        {item.last_successful_sync_at === null
                          ? "ещё не синхронизирован"
                          : `синхр. ${formatDate(item.last_successful_sync_at)}`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="muted cp-count">Ящиков: {items.length}</p>
          </>
        ) : null}
      </div>

      <div className="cp-detail">
        {creating && canCreate ? (
          <CreateMailbox
            service={service}
            onCancel={() => onSearch({ id: undefined })}
            onCreated={(created) => onSearch({ id: created.id })}
          />
        ) : opened !== undefined ? (
          <MailboxCard
            key={opened}
            service={service}
            id={opened}
            canSync={canSync}
            canViewFailures={canViewFailures}
            canSelectSystemSender={canSelectSystemSender}
            organizationName={organizationName}
            onBack={() => onSearch({ id: undefined })}
            onShowFailures={(mailboxId) =>
              onSearch({
                tab: "failures",
                mailbox: mailboxId,
                id: undefined,
                failure: undefined,
              })
            }
          />
        ) : (
          <div className="cp-empty cp-empty--detail">
            <p>
              Выберите ящик в списке, чтобы увидеть настройки, проверить
              соединение и запустить синхронизацию.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function MailboxesPage({ services }: { services: ShellServices }) {
  const navigate = useNavigate();
  const locationSearch = useLocation({
    select: (location) => location.search as Record<string, unknown>,
  });
  const requested = validateMailboxesSearch(locationSearch);
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const service = services.mailboxes;
  // Ящик принадлежит организации (КПО): в режиме «Все организации» без этой
  // подписи не понять, чей он.
  const scopes = bootstrap.data?.scopes;
  const organizationName = useCallback<OrganizationName>(
    (id) => scopes?.find((scope) => scope.id === id)?.name ?? null,
    [scopes],
  );
  const permissions = bootstrap.data?.permissions ?? [];
  const can = (permission: string) =>
    hasPermission({ permissions }, permission);
  const canManage = can("document-workflow.mailboxes.manage");
  const canViewFailures = can("document-workflow.failures.manage");
  const canSync = can("document-workflow.mail.sync");
  const tabs = (
    [
      canManage ? "mailboxes" : null,
      canViewFailures ? "failures" : null,
    ] as const
  ).filter((tab): tab is MailboxesTab => tab !== null);

  // Вкладку, к которой нет доступа, заменяет первая доступная.
  const search: MailboxesSearch = tabs.includes(requested.tab)
    ? requested
    : { ...requested, tab: tabs[0] ?? "mailboxes" };

  // Новый ящик создаётся в одной организации, и в режиме «Все организации»
  // выбрать её здесь не из чего: Core ответил бы конфликтом.
  const allOrganizations = bootstrap.data?.allOrganizations === true;
  const canCreate = canManage && !allOrganizations;

  // Адресная строка — единственный источник состояния; поле со значением
  // undefined в патче убирается из неё.
  function setSearch(patch: Partial<MailboxesSearch>) {
    const next = validateMailboxesSearch({ ...search, ...patch });
    void navigate({
      to: "/administration/mailboxes",
      search: compactMailboxesSearch(next),
      resetScroll: false,
    });
  }

  if (service === undefined) return <ErrorPage status={503} />;

  function selectTab(tab: MailboxesTab) {
    setSearch({ tab, id: undefined, failure: undefined });
  }

  return (
    <section
      className="workspace-page cp-page mb-page"
      aria-labelledby="mb-title"
    >
      <div className="dw-heading">
        <div>
          <p className="eyebrow">Администрирование</p>
          <h1 id="mb-title">Почтовые ящики</h1>
          <p>
            Ящики, из которых документооборот получает письма, и журнал ошибок
            загрузки. Пароли и секреты здесь не показываются.
          </p>
        </div>
        {canCreate && search.tab === "mailboxes" ? (
          <button
            type="button"
            className="button button--primary"
            onClick={() => setSearch({ id: newMailboxId })}
          >
            Добавить ящик
          </button>
        ) : null}
      </div>

      {tabs.length > 1 ? (
        <TabList
          tabs={tabs}
          labels={tabLabels}
          active={search.tab}
          onSelect={selectTab}
          label="Разделы почтового администрирования"
          idPrefix="mb-tab"
          panelId="mb-panel"
        />
      ) : null}

      <div
        id="mb-panel"
        role={tabs.length > 1 ? "tabpanel" : undefined}
        aria-labelledby={tabs.length > 1 ? `mb-tab-${search.tab}` : undefined}
        className="cp-panel"
      >
        {search.tab === "mailboxes" && canManage ? (
          <MailboxDirectory
            service={service}
            canCreate={canCreate}
            canSync={canSync}
            canViewFailures={canViewFailures}
            canSelectSystemSender={
              bootstrap.data?.user.is_system_administrator === true
            }
            allOrganizations={allOrganizations}
            scopeName={bootstrap.data?.currentScope?.name}
            organizationName={organizationName}
            search={search}
            onSearch={setSearch}
          />
        ) : null}
        {search.tab === "failures" && canViewFailures ? (
          <ImportFailuresPanel
            service={service}
            canViewMailboxes={canManage}
            organizationName={organizationName}
            search={search}
            onSearch={setSearch}
          />
        ) : null}
      </div>
    </section>
  );
}
